import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, getCurrentUser } from '../services/api';
import { setUser as setSentryUser } from '../utils/sentry';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      // If running in Cypress, use test user instead of real auth
      if (window.Cypress) {
        const testUser = window.Cypress.env('TEST_USER') || 'gary';
        const testUsers = {
          'gary': {
            id: 1,
            username: 'gary',
            email: 'gary@wolfdata.com',
            full_name: 'Gary Stanton',
            role: 'super_admin',
            avatar_color: '#10b981',
            is_active: true
          },
          'john': {
            id: 2,
            username: 'john',
            email: 'john@wolfdata.com',
            full_name: 'John Smith',
            role: 'case_agent',
            avatar_color: '#3b82f6',
            is_active: true
          },
          'admin': {
            id: 3,
            username: 'admin',
            email: 'admin@wolfdata.com',
            full_name: 'Admin User',
            role: 'admin',
            avatar_color: '#8b5cf6',
            is_active: true
          },
          'lead': {
            id: 4,
            username: 'lead',
            email: 'lead@wolfdata.com',
            full_name: 'Lead Agent',
            role: 'lead',
            avatar_color: '#f59e0b',
            is_active: true
          }
        };
        
        console.log('🧪 Cypress Test Mode: Setting user to', testUser);
        setUser(testUsers[testUser] || testUsers['gary']);
        setLoading(false);
        return;
      }
      
      // Normal auth flow for non-test environments
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const userData = await getCurrentUser();
          setUser(userData);
          // Set Sentry user context
          setSentryUser(userData);
        } catch (err) {
          console.error('Failed to get current user:', err);
          localStorage.removeItem('auth_token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password) => {
    setError(null);
    try {
      const { access_token } = await apiLogin(username, password);
      localStorage.setItem('auth_token', access_token);
      
      // Get user info
      const userData = await getCurrentUser();
      setUser(userData);
      
      // Set Sentry user context
      setSentryUser(userData);
      
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const setTokenAndLoad = async (token) => {
    localStorage.setItem('auth_token', token);
    const userData = await getCurrentUser();
    setUser(userData);
    setSentryUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('currentUser'); // Clear old Redux user cache
    setUser(null);
    
    // Clear Sentry user context
    setSentryUser(null);
  };

  // Watch for Cypress test user changes
  useEffect(() => {
    if (!window.Cypress) return;
    
    const checkTestUserChange = setInterval(() => {
      const currentTestUser = window.Cypress.env('TEST_USER') || 'gary';
      if (user?.username !== currentTestUser) {
        const testUsers = {
          'gary': { id: 1, username: 'gary', email: 'gary@wolfdata.com', full_name: 'Gary Stanton', role: 'super_admin', avatar_color: '#10b981', is_active: true },
          'john': { id: 2, username: 'john', email: 'john@wolfdata.com', full_name: 'John Smith', role: 'case_agent', avatar_color: '#3b82f6', is_active: true },
          'admin': { id: 3, username: 'admin', email: 'admin@wolfdata.com', full_name: 'Admin User', role: 'admin', avatar_color: '#8b5cf6', is_active: true },
          'lead': { id: 4, username: 'lead', email: 'lead@wolfdata.com', full_name: 'Lead Agent', role: 'lead', avatar_color: '#f59e0b', is_active: true }
        };
        console.log('🧪 Cypress: Switching to user', currentTestUser);
        setUser(testUsers[currentTestUser] || testUsers['gary']);
      }
    }, 100);
    
    return () => clearInterval(checkTestUserChange);
  }, [user]);

  const value = {
    user,
    loading,
    error,
    login,
    setTokenAndLoad,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin',
    isLead: user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'lead',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
